import type { Color, Layer } from '@deck.gl/core';
import { ScatterplotLayer } from '@deck.gl/layers';

import type { HubDeviceHealth } from './statusTelemetryApi';
import type { HubDeviceMarkerDatum } from './deviceHealthProjection';

export type DeviceHealthDeckPalette = Record<HubDeviceHealth, Color> & {
  selected: Color;
};

export const HUB_DEVICE_STATUS_RADIUS_PIXELS = 7;
export const HUB_DEVICE_STATUS_MIN_RADIUS_PIXELS = 3;
export const HUB_DEVICE_STATUS_MIN_ZOOM = 4;
export const HUB_DEVICE_STATUS_MAX_ZOOM = 12;
export const HUB_DEVICE_SELECTION_GAP_PIXELS = 3;

export const hubDeviceStatusRadiusForZoom = (zoom: number): number => {
  const boundedZoom = Math.min(
    HUB_DEVICE_STATUS_MAX_ZOOM,
    Math.max(HUB_DEVICE_STATUS_MIN_ZOOM, zoom)
  );
  const zoomProgress =
    (boundedZoom - HUB_DEVICE_STATUS_MIN_ZOOM) /
    (HUB_DEVICE_STATUS_MAX_ZOOM - HUB_DEVICE_STATUS_MIN_ZOOM);
  return (
    HUB_DEVICE_STATUS_MIN_RADIUS_PIXELS +
    zoomProgress * (HUB_DEVICE_STATUS_RADIUS_PIXELS - HUB_DEVICE_STATUS_MIN_RADIUS_PIXELS)
  );
};

const parseHexColor = (value: string): Color | null => {
  const match = value.trim().match(/^#([\da-f]{2})([\da-f]{2})([\da-f]{2})([\da-f]{2})?$/i);
  if (!match) return null;
  return [
    Number.parseInt(match[1], 16),
    Number.parseInt(match[2], 16),
    Number.parseInt(match[3], 16),
    match[4] ? Number.parseInt(match[4], 16) : 255
  ];
};

const semanticColor = (styles: CSSStyleDeclaration, token: string): Color => {
  const parsed = parseHexColor(styles.getPropertyValue(token));
  return parsed ?? Array.from(new Uint8ClampedArray(4));
};

export const readDeviceHealthDeckPalette = (
  element: Element = document.documentElement
): DeviceHealthDeckPalette => {
  const styles = getComputedStyle(element);
  return {
    healthy: semanticColor(styles, '--color-success'),
    degraded: semanticColor(styles, '--color-warning'),
    critical: semanticColor(styles, '--color-error'),
    unknown: semanticColor(styles, '--text-muted'),
    selected: semanticColor(styles, '--color-info')
  };
};

export const buildHubDeviceHealthLayers = ({
  data,
  selectedHubID,
  palette,
  zoom
}: {
  data: HubDeviceMarkerDatum[];
  selectedHubID?: string | null;
  palette: DeviceHealthDeckPalette;
  zoom: number;
}): Layer[] => {
  if (!data.length) return [];
  const statusRadius = hubDeviceStatusRadiusForZoom(zoom);
  const selected = selectedHubID
    ? data.filter(datum => datum.id === selectedHubID)
    : [];

  return [
    new ScatterplotLayer<HubDeviceMarkerDatum>({
      id: 'hub-device-selection-ring',
      data: selected,
      getPosition: datum => datum.position,
      getRadius: statusRadius + HUB_DEVICE_SELECTION_GAP_PIXELS,
      radiusUnits: 'pixels',
      filled: false,
      stroked: true,
      getLineColor: palette.selected,
      getLineWidth: 2,
      lineWidthUnits: 'pixels',
      pickable: false
    }),
    new ScatterplotLayer<HubDeviceMarkerDatum>({
      id: 'hub-device-status-dot',
      data,
      getPosition: datum => datum.position,
      getRadius: statusRadius,
      radiusUnits: 'pixels',
      filled: true,
      stroked: false,
      getFillColor: datum => palette[datum.summary.overall],
      pickable: true
    })
  ];
};
